import React from 'react';
import { Box, Container, Typography, Grid, Avatar, useTheme, alpha, Card, Stack, Chip, Button } from '@mui/material';
import { FormatQuote, ArrowForward } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const founderNote = {
  quote: "I built Nexus because I was tired of paying for five different tools that didn't talk to each other. This is the platform I wished existed when I started my own practice.",
  name: 'Sam S.',
  role: 'Founder, FutureSense',
  initial: 'S'
};

const TestimonialsSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const renderFounderCard = () => {
    const accent = theme.palette.primary.main;
    return (
      <Card
        sx={{
          ...theme.customEffects?.glass,
          p: 4,
          height: '100%',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          '&:hover': {
            borderColor: alpha(accent, 0.3),
            transform: 'translateY(-4px)',
          },
        }}
      >
        <Chip
          label="FOUNDER NOTE"
          size="small"
          sx={{
            bgcolor: alpha(accent, 0.12),
            color: accent,
            fontWeight: 800,
            letterSpacing: '0.08em',
            fontSize: '0.7rem',
            mb: 2,
          }}
        />
        <FormatQuote
          sx={{
            fontSize: 40,
            color: alpha(accent, 0.2),
            mb: 2,
            transform: 'rotate(180deg)',
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: 1.7,
            mb: 4,
            fontStyle: 'italic',
            fontSize: '1.05rem',
          }}
        >
          "{founderNote.quote}"
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: alpha(accent, 0.1),
              color: accent,
              fontWeight: 800,
              fontSize: '0.9rem',
            }}
          >
            {founderNote.initial}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffffff' }}>
              {founderNote.name}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              {founderNote.role}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  const renderEarlyAccessCard = () => {
    const accent = theme.palette.secondary.main;
    return (
      <Card
        sx={{
          ...theme.customEffects?.glass,
          p: 4,
          height: '100%',
          border: `1px dashed ${alpha(accent, 0.4)}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:hover': {
            borderColor: alpha(accent, 0.7),
            borderStyle: 'solid',
            transform: 'translateY(-4px)',
          },
        }}
      >
        <Box>
          <Chip
            label="EARLY ACCESS"
            size="small"
            sx={{
              bgcolor: alpha(accent, 0.12),
              color: accent,
              fontWeight: 800,
              letterSpacing: '0.08em',
              fontSize: '0.7rem',
              mb: 2,
            }}
          />
          <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 800, mb: 2, lineHeight: 1.25 }}>
            Be one of our first practitioners.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, mb: 3 }}>
            We're building this with the people who'll use it every day. Early users get
            founder pricing locked in, direct line to the team, and a say in the roadmap.
          </Typography>
          <Stack spacing={1.2}>
            {[
              'Lock in $19.99/mo Pro pricing for life',
              'Direct Slack/email access to the founder',
              'Help shape what ships next',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    bgcolor: accent,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        <Button
          variant="outlined"
          endIcon={<ArrowForward />}
          onClick={() => navigate('/signup')}
          sx={{
            mt: 4,
            borderColor: alpha(accent, 0.5),
            color: '#ffffff',
            fontWeight: 700,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
        >
          Claim Founder Pricing
        </Button>
      </Card>
    );
  };

  return (
    <Box sx={{ py: { xs: 10, md: 15 }, background: '#000000', position: 'relative' }}>
      <Container maxWidth="lg">
        <Box textAlign="center" sx={{ mb: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                letterSpacing: 2,
                mb: 2,
                display: 'block'
              }}
            >
              The New Standard
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                mb: 3,
                fontSize: { xs: '2.2rem', md: '3.2rem' },
                letterSpacing: '-0.04em'
              }}
            >
              Built by a practitioner. Looking for the next ones.
            </Typography>
          </motion.div>
        </Box>

        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              {renderFounderCard()}
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              {renderEarlyAccessCard()}
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
